import {Component, EventEmitter, Input, Output} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent, ConfirmationModalComponent} from "@components";
import {EventItem, EventPaymentSettings, Lookups, OrganiserPaymentData, Race, RaceCommercialItem, TOAST_STATE} from "@model";
import {RaceCommercialService, RaceCommercialServiceErrors, ToastService} from "@services";
import {RaceCommercialItemFormComponent} from "../race-commercial-item-form/race-commercial-item-form.component";

@Component({
  selector: 'app-race-commercial-item-card',
  templateUrl: './race-commercial-item-card.component.html',
  styleUrls: ['./race-commercial-item-card.component.scss']
})
export class RaceCommercialItemCardComponent extends AbstractComponent {
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() disableEditing: boolean = false;
  @Input() raceCommercialItems: RaceCommercialItem[] = [];
  @Input() eventPaymentSettings: EventPaymentSettings | null = null;
  @Input() organiserPaymentData: OrganiserPaymentData | null = null;
  @Input() currencies: Lookups[] = [];
  @Input() registrationFeeTypes: Lookups[] = [];
  @Input({required: true}) languages: Lookups[] = [];

  @Output() raceCommercialItemsChanged: EventEmitter<RaceCommercialItem[]> = new EventEmitter<RaceCommercialItem[]>();
  raceCommericalItemsCollapsed: boolean = false;

  constructor(public translocoService: TranslocoService, private toastService: ToastService,
              private raceCommercialService: RaceCommercialService, private modalService: NgbModal,
  ) {
    super();
  }

  onDeleteRaceCommercialItem(raceCommercialItem: RaceCommercialItem) {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });

    modalRef.componentInstance.title = this.translocoService.translate("race.pricing.item.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("race.pricing.item.delete.dialog.text");

    modalRef.result.then((_) => {
      this.deleteRaceCommercialItem(raceCommercialItem);
    }, (_) => {
      /// ignore
    });
  }

  deleteRaceCommercialItem(raceCommercialItem: RaceCommercialItem) {
    raceCommercialItem.raceId = this.race?.id;
    this.raceCommercialService.deleteRaceCommercialItem(raceCommercialItem,
      () => {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("race.pricing.item.delete.success"));
        this.raceCommercialService.findRaceCommercialItems(this.race!.id!).subscribe((raceCommercialItems: RaceCommercialItem[]) => {
          this.raceCommercialItemsChanged.emit(this.raceCommercialService.sortRaceCommercialItem(raceCommercialItems));
        });
      }, (errorCode: number) => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate(errorCode === RaceCommercialServiceErrors.COMMERCIAL_ITEM_IN_USE ? "race.pricing.item.delete.error.17" :"race.pricing.item.delete.error"))
      });
  }

  getCurrencyLabel(): string {
    const curr = this.currencies.find((currency) => currency.id === this.event?.currency);
    return curr?.name ?? "";
  }

  openEditModal(item?: RaceCommercialItem) {
    const modalRef = this.modalService.open(RaceCommercialItemFormComponent, {
      centered: true,
      size: 'lg',
      scrollable: true,
      windowClass: 'race-commercial-modal',
    });
    modalRef.componentInstance.title = this.translocoService.translate(item ? "race.pricing.editRegistrationFee" : "race.pricing.addRegistrationFee");
    modalRef.componentInstance.currentRaceCommercialItem = item ?? new RaceCommercialItem();
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.race = this.race;
    modalRef.componentInstance.currencies = this.currencies;
    modalRef.componentInstance.registrationFeeTypes = this.registrationFeeTypes;
    modalRef.componentInstance.raceCommercialItems = this.raceCommercialItems;
    modalRef.componentInstance.organiserPaymentData = this.organiserPaymentData;
    modalRef.componentInstance.disableEditing = this.disableEditing;
    modalRef.result.then((raceCommercialItem: RaceCommercialItem) => {
      this.raceCommercialService.updateRaceCommercialItem(raceCommercialItem, () => {
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("race.pricing.item.save.success"));
        this.raceCommercialService.findRaceCommercialItems(this.race!.id!).subscribe((raceCommercialItems: RaceCommercialItem[]) => {
          this.onRaceCommercialItemsChanged(raceCommercialItems);
        });
      }, () => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("race.pricing.item.save.error"));
      });
    }, (_) => {
      /// ignore
    });
  }

  onRaceCommercialItemsChanged(raceCommercialItems: RaceCommercialItem[]): void {
    this.raceCommercialItemsChanged.emit(raceCommercialItems);
  }
}
